import axios from 'axios';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import qs from 'query-string';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import accountService from 'services/account';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import formativesService from 'services/formative';
import rubricService from 'services/rubric';
import { resolveFeatureFlags } from 'utils/helper';

const AppContext = createContext();
const useApp = () => useContext(AppContext);
const ACTIVE_ROSTER_SYNC_STATUSES = ['queued', 'running'];
const MAX_ROSTER_SYNC_ACTIVE_MS = 30 * 60 * 1000;
const PENDING_AUTO_ROSTER_SYNC_KEY = 'pending_auto_roster_sync';

const isActiveRosterSyncStatus = (status) => ACTIVE_ROSTER_SYNC_STATUSES.includes(status);

const isStaleRosterSync = (course) => {
    if (!isActiveRosterSyncStatus(course?.roster_sync_status)) return false;
    const lastActiveOn = course.roster_sync_heartbeat_on || course.roster_sync_started_on;
    if (!lastActiveOn) return false;
    return Date.now() - lastActiveOn > MAX_ROSTER_SYNC_ACTIVE_MS;
};

const hasRosterSyncStateChanged = (currentCourse, nextCourse) => {
    if (!currentCourse || !nextCourse) return true;

    return (
        currentCourse.roster_sync_status !== nextCourse.roster_sync_status ||
        currentCourse.roster_sync_started_on !== nextCourse.roster_sync_started_on ||
        currentCourse.roster_sync_heartbeat_on !== nextCourse.roster_sync_heartbeat_on ||
        currentCourse.roster_sync_finished_on !== nextCourse.roster_sync_finished_on ||
        JSON.stringify(currentCourse.roster_sync_progress || null) !==
            JSON.stringify(nextCourse.roster_sync_progress || null) ||
        JSON.stringify(currentCourse.roster_sync_summary || null) !==
            JSON.stringify(nextCourse.roster_sync_summary || null) ||
        currentCourse.roster_sync_error !== nextCourse.roster_sync_error
    );
};

const mapFeedbackToStudentSubmissions = (feedback = []) => {
    return feedback
        .map((item) => {
            if (!item.files) return { ...item, files: [item.submission] };
            return item;
        })
        .map((item) => {
            return omit(
                {
                    ...item,
                    ai: {
                        feedback: item.feedback,
                        grading: item.grading,
                        inline_comments: item.inline_comments,
                    },
                    is_legacy: true,
                    type: 'formative',
                },
                ['feedback', 'grading', 'inline_comments']
            );
        });
};

const buildStudentSubmissions = (submissions = [], feedback = []) => {
    return [...submissions, ...mapFeedbackToStudentSubmissions(feedback)].sort((a, b) => a.created_on - b.created_on);
};

const getPendingAutoRosterSync = () => {
    try {
        const value = sessionStorage.getItem(PENDING_AUTO_ROSTER_SYNC_KEY);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
};

const getPendingAutoRosterSyncForCourse = (courseId) => {
    const pendingAutoRosterSync = getPendingAutoRosterSync();
    if (pendingAutoRosterSync?.courseId !== `${courseId || ''}`) return null;

    const triggeredOn = Number(pendingAutoRosterSync.triggeredOn || 0);
    if (!triggeredOn || Date.now() - triggeredOn > MAX_ROSTER_SYNC_ACTIVE_MS) {
        sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
        return null;
    }

    return {
        ...pendingAutoRosterSync,
        triggeredOn,
    };
};

const mergeAccountsAndUsers = (accounts, users) => {
    return accounts
        .filter(({ active }) => active)
        .map((account) => {
            const user = users.find(({ id }) => id === account.user_id);
            if (!user) return null;
            const userAccounts = accounts.filter(({ user_id }) => user_id === user.id);
            const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
            return {
                ...omit(account, ['_id', 'id']),
                ...omit(user, ['_id', 'id']),
                owner_account_id: account.id,
                user_id: user.id,
                is_pending: isPending,
            };
        })
        .filter(Boolean);
};

const AppProvider = (props) => {
    const { auth, setAuth } = useAuth();
    const location = useLocation();
    const { setPingPayload, onMessage } = useWebsocket();

    // default value null means uninitialized unless stated otherwise
    const [course, setCourse] = useState(null);
    const [accountUsers, setAccountUsers] = useState(null); // account document and user document merged
    const [assignments, setAssignments] = useState(null);
    const [currentAssignmentId, setCurrentAssignmentId] = useState(null); // the assignment you are currently looking at
    const [batch, setBatch] = useState(null); // ongoing backend jobs for current assignment
    const [formative, setFormative] = useState(undefined); // formative for current assignment and current accountUser
    const [submissions, setSubmissions] = useState(null); // submissions for current assignment
    const [previewSubmissions, setPreviewSubmissions] = useState(null); // preview submissions for current assignment, used for fine-tuning
    const [studentSubmissions, setStudentSubmissions] = useState(null); // student submissions for current assignment
    const [rubrics, setRubrics] = useState(null);
    const [department, setDepartment] = useState(null);

    const isLoading = !course || !accountUsers || !assignments;
    const selfAccountUser = useMemo(
        () => accountUsers?.find(({ owner_account_id }) => owner_account_id === auth.id),
        [accountUsers, auth?.id]
    );
    const instructorAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor'),
        [accountUsers]
    );
    const staffAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor' || role === 'ta'),
        [accountUsers]
    );
    const studentAccountUsers = accountUsers?.filter(({ active, role }) => active && role === 'student');
    const currentAssignment = assignments?.find(({ id }) => id === currentAssignmentId);

    const query = useMemo(() => qs.parse(location.search), [location.search]);
    const formativesTargetOwnerAccountId = useMemo(() => {
        if (auth?.role === 'student') return auth?.id;
        const isFormativeRoute = location.pathname.endsWith('/submissions') && query.type === 'formative';
        if (isFormativeRoute) return query.owner_account_id;
        return null;
    }, [auth?.id, auth?.role, location.pathname, query.owner_account_id, query.type]);

    const isGradingFeatureDisabled = useMemo(() => department?.features?.assignment_grading === false, [department]);
    const isFeedbackFeatureDisabled = useMemo(() => department?.features?.assignment_feedback === false, [department]);
    const isAcceptAllFeatureDisabled = useMemo(
        () => department?.features?.assignment_accept_all === false,
        [department]
    );
    const isInlineCommentsFeatureDisabled = useMemo(
        () => department?.features?.assignment_inline_comments === false,
        [department]
    );
    const isRubricFeaturedDisabled = useMemo(() => department?.features?.assignment_rubric === false, [department]);

    const updateCourse = async (courseData) => {
        try {
            await courseService.updateCourse(auth.course_id, courseData);
            return setCourse((prev) => ({ ...prev, ...courseData }));
        } catch (e) {
            console.error(e);
        }
    };

    const refreshCourseAccountUsers = useCallback(async (courseId, { retries = 0, retryDelayMs = 1500 } = {}) => {
        const getMergedAccountUsers = async () => {
            const [accounts, users] = await Promise.all([
                courseService.listCourseAccounts(courseId),
                courseService.listCourseUsers(courseId),
            ]);

            return {
                activeAccountCount: accounts.filter(({ active }) => active).length,
                mergedAccountUsers: mergeAccountsAndUsers(accounts, users),
            };
        };

        let attempt = 0;
        let result = await getMergedAccountUsers();

        while (result.mergedAccountUsers.length < result.activeAccountCount && attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            result = await getMergedAccountUsers();
            attempt += 1;
        }

        return result.mergedAccountUsers;
    }, []);

    const addAccountUser = (accountUser) => {
        setAccountUsers((current) => [...current, accountUser]);
    };

    const removeAccountUser = (accountUser) => {
        setAccountUsers((current) =>
            current.filter(({ owner_account_id }) => owner_account_id !== accountUser.owner_account_id)
        );
    };

    const changeAccountUserRole = async (targetAccountUser, newRole) => {
        const newAccount = await accountService.updateAccount(targetAccountUser.owner_account_id, {
            role: newRole,
        });
        setAccountUsers((current) => {
            return current.map((accountUser) => {
                if (accountUser.owner_account_id !== targetAccountUser.owner_account_id) return accountUser;
                return {
                    ...accountUser,
                    ...omit(newAccount, ['_id', 'id']),
                    owner_account_id: newAccount.id,
                };
            });
        });
        if (targetAccountUser.owner_account_id === auth.id) {
            setAuth((current) => ({ ...current, ...newAccount }));
        }
    };

    useEffect(() => {
        if (!auth?.course_id) return;
        (async () => {
            const course = await courseService.findCourse(auth.course_id);
            setCourse(course);
        })();
    }, [auth?.course_id]);

    useEffect(() => {
        if (!auth?.course_id) return;
        if (!location.pathname.startsWith('/people')) return;

        let isMounted = true;

        (async () => {
            try {
                const latestCourse = await courseService.findCourse(auth.course_id);
                if (!isMounted || !latestCourse) return;

                const pendingAutoRosterSync = getPendingAutoRosterSyncForCourse(auth.course_id);
                const isLatestRosterSyncActive = isActiveRosterSyncStatus(latestCourse.roster_sync_status);
                const pendingAutoRosterSyncFinished =
                    pendingAutoRosterSync &&
                    latestCourse.roster_sync_finished_on &&
                    latestCourse.roster_sync_finished_on >= pendingAutoRosterSync.triggeredOn;
                const shouldRefreshAccountUsers =
                    !accountUsers ||
                    (hasRosterSyncStateChanged(course, latestCourse) && !isLatestRosterSyncActive) ||
                    pendingAutoRosterSyncFinished;

                if (shouldRefreshAccountUsers) {
                    const mergedAccountUsers = await refreshCourseAccountUsers(auth.course_id, { retries: 5 });
                    if (!isMounted) return;
                    setAccountUsers(mergedAccountUsers);
                    if (pendingAutoRosterSyncFinished) {
                        sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
                    }
                }

                setCourse((current) => (hasRosterSyncStateChanged(current, latestCourse) ? latestCourse : current));
            } catch (error) {
                console.error('Failed to refresh course state for People page:', error);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [
        accountUsers,
        auth?.course_id,
        course,
        course?.id,
        course?.roster_sync_error,
        course?.roster_sync_finished_on,
        course?.roster_sync_heartbeat_on,
        course?.roster_sync_started_on,
        course?.roster_sync_status,
        location.pathname,
        refreshCourseAccountUsers,
    ]);
    useEffect(() => {
        if (!course?.id) return;
        (async () => {
            const [accounts, users, assignments, department] = await Promise.all([
                await courseService.listCourseAccounts(course.id),
                await courseService.listCourseUsers(course.id),
                await courseService.listCourseAssignments(course.id),
                await courseService.findCourseDepartment(course.id),
            ]);
            setAccountUsers(mergeAccountsAndUsers(accounts, users));
            setAssignments(assignments.map((assignment) => resolveFeatureFlags(assignment, department?.features)));
            setDepartment(department);
        })();
    }, [course?.id]);

    useEffect(() => {
        if (!course?.id) return;
        const pendingAutoRosterSync = getPendingAutoRosterSyncForCourse(course.id);
        const isCourseRosterSyncActive = isActiveRosterSyncStatus(course.roster_sync_status);
        if (!isCourseRosterSyncActive && !pendingAutoRosterSync) return;
        if (isCourseRosterSyncActive && isStaleRosterSync(course)) return;

        let isMounted = true;
        let isRefreshing = false;
        let intervalId = null;

        const refreshRosterSyncState = async () => {
            if (isRefreshing) return;
            if (pendingAutoRosterSync && Date.now() - pendingAutoRosterSync.triggeredOn > MAX_ROSTER_SYNC_ACTIVE_MS) {
                sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
                if (intervalId) {
                    clearInterval(intervalId);
                }
                return;
            }

            isRefreshing = true;
            try {
                const latestCourse = await courseService.findCourse(course.id);
                if (!isMounted || !latestCourse) return;

                const latestRosterSyncActive = isActiveRosterSyncStatus(latestCourse.roster_sync_status);
                const pendingAutoRosterSyncFinished =
                    pendingAutoRosterSync &&
                    latestCourse.roster_sync_finished_on &&
                    latestCourse.roster_sync_finished_on >= pendingAutoRosterSync.triggeredOn;
                const isSyncCompleted =
                    (!pendingAutoRosterSync && !latestRosterSyncActive) ||
                    pendingAutoRosterSyncFinished ||
                    isStaleRosterSync(latestCourse);

                if (isSyncCompleted) {
                    const mergedAccountUsers = await refreshCourseAccountUsers(course.id, { retries: 5 });
                    if (!isMounted) return;
                    setAccountUsers(mergedAccountUsers);
                    setCourse(latestCourse);
                    if (pendingAutoRosterSyncFinished) {
                        sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
                    }
                    if (intervalId) {
                        clearInterval(intervalId);
                    }
                    return;
                }

                setCourse((current) => {
                    if (!current) return latestCourse;

                    return hasRosterSyncStateChanged(current, latestCourse) ? latestCourse : current;
                });
            } catch (error) {
                console.error('Failed to refresh roster sync state:', error);
            } finally {
                isRefreshing = false;
            }
        };

        refreshRosterSyncState();
        intervalId = setInterval(refreshRosterSyncState, 5000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [
        course,
        course?.id,
        course?.roster_sync_error,
        course?.roster_sync_finished_on,
        course?.roster_sync_heartbeat_on,
        course?.roster_sync_started_on,
        course?.roster_sync_status,
        refreshCourseAccountUsers,
    ]);

    useEffect(() => {
        if (location.pathname.startsWith('/assignments')) {
            const assignmentId = location.pathname.split('/')[2];
            if (!assignmentId) {
                setCurrentAssignmentId(null);
            }
            setCurrentAssignmentId(assignmentId);
            const currentAssignment = assignments?.find(({ id }) => id === assignmentId);
            if (currentAssignment?.api_assignment_id) {
                try {
                    const PLATFORM = process.env.REACT_APP_KIND;
                    (async () => {
                        const accessToken = localStorage.getItem('access_token');
                        const apiAssignment = await apiService.getAssignment({
                            course_id: auth.course_id,
                            accessToken,
                            assignment_id: currentAssignment.api_assignment_id,
                        });
                        const pastPointsPossible =
                            currentAssignment.api_data?.points_possible ||
                            currentAssignment.api_data?.Assessment?.ScoreDenominator;
                        const newPointsPossible =
                            apiAssignment?.points_possible || apiAssignment?.Assessment?.ScoreDenominator;

                        if (pastPointsPossible !== newPointsPossible) {
                            if (PLATFORM === 'CANVAS') {
                                await assignmentService.updateAssignment(currentAssignment.id, {
                                    api_data: {
                                        ...currentAssignment.api_data,
                                        points_possible: newPointsPossible,
                                    },
                                });
                                setAssignments((current) =>
                                    current.map((assignment) => {
                                        if (assignment.id !== currentAssignment.id) return assignment;
                                        return {
                                            ...assignment,
                                            api_data: {
                                                ...assignment.api_data,
                                                points_possible: newPointsPossible,
                                            },
                                        };
                                    })
                                );
                            } else if (PLATFORM === 'D2L') {
                                await assignmentService.updateAssignment(currentAssignment.id, {
                                    api_data: {
                                        ...currentAssignment.api_data,
                                        Assessment: {
                                            ...currentAssignment.api_data?.Assessment,
                                            ScoreDenominator: apiAssignment.Assessment.ScoreDenominator,
                                        },
                                    },
                                });
                                setAssignments((current) =>
                                    current.map((assignment) => {
                                        if (assignment.id !== currentAssignment.id) return assignment;
                                        return {
                                            ...assignment,
                                            api_data: {
                                                ...assignment.api_data,
                                                Assessment: {
                                                    ...assignment.api_data?.Assessment,
                                                    ScoreDenominator: apiAssignment.Assessment.ScoreDenominator,
                                                },
                                            },
                                        };
                                    })
                                );
                            }
                        }
                    })();
                } catch (e) {
                    console.log('Error fetching API assignment:', e);
                }
            }
        } else {
            setCurrentAssignmentId(null);
        }
    }, [assignments, auth?.course_id, location?.pathname]);

    useEffect(() => {
        setBatch(null);
        setSubmissions(null);
        setPreviewSubmissions(null);
        setStudentSubmissions(null);

        if (!auth || !currentAssignmentId || !instructorAccountUsers) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                const instructorOwnerAccountIds = instructorAccountUsers.map(
                    ({ owner_account_id }) => owner_account_id
                );
                const [batch, feedback, submissions, previewSubmissions, studentSubmissions] = await Promise.all([
                    assignmentService.listAssignmentBatch(
                        currentAssignmentId,
                        JSON.stringify({
                            is_finished: false,
                            ...(auth.role === 'student'
                                ? { owner_account_id: auth.id, type: 'formative' }
                                : { owner_account_id: instructorOwnerAccountIds }),
                        }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentFeedback(
                        currentAssignmentId,
                        auth.role === 'student' ? JSON.stringify({ owner_account_id: auth.id }) : undefined,
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        JSON.stringify({ type: 'fine-tune' }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        JSON.stringify({
                            type: 'formative',
                            ...(auth.role === 'student' ? { owner_account_id: auth.id } : {}),
                        }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                ]);
                setBatch(
                    batch.map((b) => {
                        if (!b.submissions) {
                            return {
                                ...b,
                                submissions: b.files.map((f) => ({
                                    submitted_at: b.created_on,
                                    files: [f],
                                })),
                            };
                        }
                        return b;
                    })
                );
                const taSectionIds = (selfAccountUser.sections || []).map((s) => `${s.id}`);
                setSubmissions(
                    submissions
                        .filter((submission) => {
                            if (auth.role === 'ta' && Boolean(currentAssignment.api_assignment_id)) {
                                const apiUser = submission.api?.user;
                                if (!apiUser) return false;
                                const studentSections = [];
                                if (apiUser.section) studentSections.push(apiUser.section);
                                if (Array.isArray(apiUser.sections)) studentSections.push(...apiUser.sections);
                                if (studentSections.length === 0) return true;
                                const studentSectionIds = studentSections.map((s) => `${s.id}`);
                                return studentSectionIds.some((sid) => taSectionIds.includes(sid));
                            }
                            return true;
                        })
                        .map((submission) => {
                            if (!submission.files) {
                                return { ...submission, files: [{ ...submission.data, api: submission.api }] };
                            }
                            return submission;
                        })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
                setStudentSubmissions(buildStudentSubmissions(studentSubmissions, feedback));
            } catch (error) {
                if (!axios.isCancel?.(error) && error.name !== 'CanceledError') {
                    console.error(error);
                }
            }
        };
        fetchData();

        return () => controller.abort();
    }, [auth, currentAssignment?.api_assignment_id, currentAssignmentId, instructorAccountUsers, selfAccountUser]);

    useEffect(() => {
        if (!auth || !currentAssignmentId || !formativesTargetOwnerAccountId) return setFormative(undefined);

        const controller = new AbortController();
        const signal = controller.signal;

        (async () => {
            try {
                const formatives = await formativesService.listFormatives(
                    JSON.stringify({
                        assignment_id: currentAssignmentId,
                        course_id: auth.course_id,
                        owner_account_id: formativesTargetOwnerAccountId,
                    }),
                    null,
                    null,
                    null,
                    { signal }
                );
                setFormative(formatives[0] || null);
            } catch (error) {
                if (!axios.isCancel?.(error) && error.name !== 'CanceledError') {
                    console.error(error);
                }
            }
        })();

        return () => controller.abort();
    }, [auth, currentAssignmentId, formativesTargetOwnerAccountId]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!currentAssignmentId || !batch) return {};
            return { batch_ids: batch.map(({ id }) => id) };
        });
        return cleanup;
    }, [currentAssignmentId, batch, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !message.batches) return;

            const isAlreadyUpToDate = (batch, list) => {
                const currentCount = list?.filter(({ batch_id }) => batch_id === batch.id).length;
                return currentCount >= batch.submission_count;
            };

            const handleFineTuneBatch = async () => {
                const previewSubmissions = await assignmentService.listAssignmentSubmissions(
                    currentAssignmentId,
                    JSON.stringify({ type: 'fine-tune' })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
                setAssignments((current) => {
                    return current.map((assignment) => {
                        if (assignment.id !== currentAssignmentId) return assignment;
                        return {
                            ...assignment,
                            preview_files: assignment.preview_files.map((pf) => {
                                const matched = previewSubmissions.find(
                                    (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                                );
                                return matched ? { ...pf, is_preview_up_to_date: true } : pf;
                            }),
                        };
                    });
                });
            };

            const handleRegularBatch = async () => {
                // support multi-file backward compatibility
                const mapDataToFiles = (arr) => {
                    return arr.map((item) => {
                        if (!item.files) {
                            item.files = [item.data];
                        }
                        return item;
                    });
                };

                const newSubmissions = await assignmentService.listAssignmentSubmissions(currentAssignmentId);

                setSubmissions((current) => {
                    const additionalSubmissions = newSubmissions.filter((item) => {
                        if (current.map(({ id }) => id).includes(item.id)) return false;
                        return true;
                    });
                    const submissionsAfterFilteringRemoved = (current ?? []).filter((item) => {
                        if (!newSubmissions.map(({ id }) => id).includes(item.id)) return false;
                        return true;
                    });
                    const final = mapDataToFiles([...submissionsAfterFilteringRemoved, ...additionalSubmissions]);
                    return final;
                });
            };

            await Promise.all(
                Object.values(message.batches).map(async (batch) => {
                    if (auth.role === 'student') {
                        if (!batch.is_finished) return;

                        const feedback = await assignmentService.listAssignmentFeedback(
                            currentAssignmentId,
                            JSON.stringify({ owner_account_id: auth.id })
                        );
                        const studentSubmissions = await assignmentService.listAssignmentSubmissions(
                            currentAssignmentId,
                            JSON.stringify({
                                type: 'formative',
                                owner_account_id: auth.id,
                            })
                        );
                        setStudentSubmissions(buildStudentSubmissions(studentSubmissions, feedback));
                    } else {
                        if (batch.type === 'fine-tune' && isAlreadyUpToDate(batch, previewSubmissions)) return;
                        if (!batch.type && isAlreadyUpToDate(batch, submissions)) return;

                        if (batch.type === 'fine-tune') {
                            await handleFineTuneBatch(batch);
                        } else {
                            await handleRegularBatch(batch);
                        }
                    }
                    if (batch.is_finished) {
                        setBatch((current) => current.filter((b) => b.id !== batch.id));
                    }
                })
            );
        });

        return cleanup;
    }, [auth?.id, auth?.role, currentAssignmentId, onMessage, previewSubmissions, submissions]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            const rubrics = await rubricService.listRubrics(
                JSON.stringify({
                    user_id: auth.user_id,
                })
            );
            setRubrics(rubrics);
        })();
    }, [auth?.user_id]);

    return (
        <AppContext.Provider
            {...props}
            value={{
                isLoading,

                // core data
                course,
                accountUsers,
                setAccountUsers,
                instructorAccountUsers, // accountUsers with role 'instructor'
                staffAccountUsers, // accountUsers with role 'instructor' or 'ta'
                studentAccountUsers, // accountUsers with role 'student'
                assignments,
                setAssignments,
                department,

                // feature flags
                isGradingFeatureDisabled,
                isFeedbackFeatureDisabled,
                isAcceptAllFeatureDisabled,
                isInlineCommentsFeatureDisabled,
                isRubricFeaturedDisabled,

                // assignment related data
                currentAssignmentId,
                setCurrentAssignmentId,
                currentAssignment,
                batch,
                setBatch,
                formative,
                setFormative,
                submissions,
                setSubmissions,
                previewSubmissions,
                setPreviewSubmissions,
                studentSubmissions,
                setStudentSubmissions,
                rubrics,
                setRubrics,

                updateCourse,
                addAccountUser,
                removeAccountUser,
                changeAccountUserRole,
            }}
        />
    );
};

export { useApp };
export default AppProvider;
